import {
  IconBan,
  IconClipboardText,
  IconTrash,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import type {
  RfqLine,
  RfqQuote,
  RfqReplyStatus,
  RfqVendor,
} from "@/lib/rfq/types";

import { Button } from "@mantine/core";
import type { QuoteItemInput, RfqPartnerOption } from "@/lib/rfq/api";
import RfqAddVendorModal from "./RfqAddVendorModal";
import RfqRecordQuoteModal from "./RfqRecordQuoteModal";
import { RfqSection } from "./RfqSection";
import { useRfqLabels } from "@/lib/rfq/labels";
import { useState } from "react";

const REPLY_STYLE: Record<RfqReplyStatus, string> = {
  pending: "bg-gray-100 text-gray-600",
  quoted: "bg-emerald-50 text-emerald-600",
  declined: "bg-red-50 text-red-600",
  overdue: "bg-amber-50 text-amber-600",
};

function RfqVendorTab({
  vendors,
  lines,
  quotes,
  partners,
  editable,
  onAddVendors,
  onDecline,
  onRemove,
  onRecord,
}: {
  vendors: RfqVendor[];
  lines: RfqLine[];
  quotes: RfqQuote[];
  partners: RfqPartnerOption[];
  editable: boolean;
  onAddVendors: (partnerIds: string[]) => Promise<void> | void;
  onDecline: (vendorId: string) => Promise<void> | void;
  onRemove: (vendorId: string) => Promise<void> | void;
  onRecord: (vendorId: string, rows: QuoteItemInput[]) => Promise<void> | void;
}) {
  const { t, reply } = useRfqLabels();
  const [recording, setRecording] = useState<RfqVendor | null>(null);
  const [adding, setAdding] = useState(false);

  const sent = vendors.length;
  const quoted = vendors.filter((v) => v.replyStatus === "quoted").length;
  const declined = vendors.filter((v) => v.replyStatus === "declined").length;
  const pending = vendors.filter(
    (v) => v.replyStatus === "pending" || v.replyStatus === "overdue",
  ).length;

  return (
    <RfqSection
      icon={IconUsers}
      title={t("rfq.vendor.sectionTitle") || "受詢廠商"}
      bodyClassName="p-0"
      action={
        <Button
          size="xs"
          radius="md"
          variant="default"
          disabled={!editable}
          leftSection={<IconUserPlus size={14} />}
          onClick={() => setAdding(true)}
        >
          {t("rfq.vendor.addVendor") || "加入廠商"}
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-gray-200 bg-gray-50/60 px-6 py-3 text-sm text-gray-600">
        <span>
          {t("rfq.vendor.sent") || "已發出"}{" "}
          <b className="text-gray-700">{sent}</b>
        </span>
        <span>
          {t("rfq.vendor.received") || "已收到"}{" "}
          <b className="text-emerald-600">{quoted}</b>
        </span>
        <span>
          {t("rfq.vendor.declined") || "婉拒"}{" "}
          <b className="text-red-500">{declined}</b>
        </span>
        <span>
          {t("rfq.vendor.pending") || "未回"}{" "}
          <b className="text-gray-700">{pending}</b>
        </span>
      </div>
      <div className="overflow-x-auto">
        {vendors.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {t("rfq.vendor.empty") || "尚無受詢廠商，點「加入廠商」從合作方選取。"}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="px-6 py-2.5 font-medium">
                  {t("rfq.vendor.colName") || "廠商名稱"}
                </th>
                <th className="px-4 py-2.5 font-medium">
                  {t("rfq.vendor.colTaxId") || "統編"}
                </th>
                <th className="px-4 py-2.5 font-medium">
                  {t("rfq.vendor.colContact") || "聯絡人"}
                </th>
                <th className="px-4 py-2.5 font-medium">
                  {t("rfq.vendor.colPhone") || "電話"}
                </th>
                <th className="px-4 py-2.5 font-medium">
                  {t("rfq.vendor.colReply") || "回覆狀態"}
                </th>
                <th className="px-6 py-2.5 text-right font-medium">
                  {t("rfq.vendor.colAction") || "操作"}
                </th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => {
                const hasQuote = quotes.some(
                  (quote) => quote.vendorId === vendor.id && quote.supplied,
                );
                return (
                  <tr
                    key={vendor.id}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="px-6 py-3 font-medium text-gray-700">
                      {vendor.name}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {vendor.taxId || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {vendor.contact || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {vendor.phone || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${REPLY_STYLE[vendor.replyStatus]}`}
                      >
                        {reply(vendor.replyStatus)}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="xs"
                          radius="md"
                          variant="subtle"
                          color="blue"
                          disabled={
                            !editable || vendor.replyStatus === "declined"
                          }
                          leftSection={<IconClipboardText size={14} />}
                          onClick={() => setRecording(vendor)}
                        >
                          {hasQuote
                            ? t("rfq.vendor.recordNegotiation") || "代錄議價"
                            : t("rfq.vendor.recordQuote") || "代錄報價"}
                        </Button>
                        <Button
                          size="xs"
                          radius="md"
                          variant="subtle"
                          color="orange"
                          disabled={
                            !editable || vendor.replyStatus === "declined"
                          }
                          leftSection={<IconBan size={14} />}
                          onClick={() => onDecline(vendor.id)}
                        >
                          {t("rfq.vendor.markDeclined") || "標記婉拒"}
                        </Button>
                        <Button
                          size="xs"
                          radius="md"
                          variant="subtle"
                          color="red"
                          disabled={!editable}
                          leftSection={<IconTrash size={14} />}
                          onClick={() => onRemove(vendor.id)}
                          aria-label={`${t("rfq.vendor.remove") || "移除"} ${vendor.name}`}
                        >
                          {t("rfq.vendor.remove") || "移除"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <RfqAddVendorModal
        open={adding}
        onClose={() => setAdding(false)}
        partners={partners}
        invitedPartnerIds={vendors
          .map((vendor) => vendor.partnerId ?? "")
          .filter(Boolean)}
        onAdd={onAddVendors}
      />

      <RfqRecordQuoteModal
        open={recording !== null}
        onClose={() => setRecording(null)}
        vendor={recording}
        lines={lines}
        quotes={quotes}
        onSubmit={onRecord}
      />
    </RfqSection>
  );
}

export default RfqVendorTab;
